import { damerauLevenshtein } from './column-matching'
import type { ParseNotice } from './column-matching'

export interface RoleOccurrence {
  /** Worksheet name; absent for CSV, which has no sheets. */
  sheet?: string
  subfunction: string
  role: string
  /** 1-based spreadsheet row. */
  row: number
  /** Absent when the row left its headcount cell blank. */
  ftes?: number
}

export interface DuplicateRoleGroup {
  subfunction: string
  role: string
  occurrences: RoleOccurrence[]
  crossSheet: boolean
}

export interface SimilarRolePair {
  subfunction: string
  roleA: string
  roleB: string
  score: number
  locationA: { sheet?: string; row: number }
  locationB: { sheet?: string; row: number }
}

/** Invalid headcount cells reported individually before the rest are summarised. */
export const MAX_INVALID_FTE_NOTICES = 10

/** Minimum whole-name similarity before two role names are suggested as the same role. */
export const SIMILAR_ROLE_THRESHOLD = 0.8

/** Names shorter than this are too short for edit distance to mean anything. */
const MIN_SIMILAR_NAME_LENGTH = 6

/** A differing word must be at least this long; shorter words are acronyms or numerals. */
const MIN_DIFFERING_TOKEN_LENGTH = 4

const MIN_TYPO_TOKEN_LENGTH = 6
const MIN_SHARED_STEM = 5
const MAX_STEM_LENGTH_DIFF = 2
const MIN_TOKEN_SIMILARITY = 0.6

const VACANCY_MARKERS = new Set(['tbd', 'tbc', '-', '–', '—', 'n/a', 'na'])

function foldKey(value: string): string {
  return value.toLowerCase().replace(/\s+/g, ' ').trim()
}

/** Case- and whitespace-insensitive (subfunction, role) key. */
export function foldRoleKey(subfunction: string, role: string): string {
  return `${foldKey(subfunction)}\u0000${foldKey(role)}`
}

function normalizeRoleName(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function sharedPrefixLength(a: string, b: string): number {
  let i = 0
  while (i < a.length && i < b.length && a[i] === b[i]) i++
  return i
}

function tokensAreVariants(a: string, b: string): boolean {
  if (a.length < MIN_DIFFERING_TOKEN_LENGTH || b.length < MIN_DIFFERING_TOKEN_LENGTH) return false
  const distance = damerauLevenshtein(a, b)
  if (distance <= 1 && a.length >= MIN_TYPO_TOKEN_LENGTH && b.length >= MIN_TYPO_TOKEN_LENGTH) return true
  const tokenSimilarity = 1 - distance / Math.max(a.length, b.length)
  return (
    sharedPrefixLength(a, b) >= MIN_SHARED_STEM &&
    Math.abs(a.length - b.length) <= MAX_STEM_LENGTH_DIFF &&
    tokenSimilarity >= MIN_TOKEN_SIMILARITY
  )
}

/** Role-name similarity in [0, 1], or 0 unless the names differ by one typo or stem variant. */
export function roleNameSimilarity(a: string, b: string): number {
  const na = normalizeRoleName(a)
  const nb = normalizeRoleName(b)
  if (na === nb) return 1
  if (na.length < MIN_SIMILAR_NAME_LENGTH || nb.length < MIN_SIMILAR_NAME_LENGTH) return 0

  const aTokens = na.split(' ')
  const bTokens = nb.split(' ')
  if (aTokens.length !== bTokens.length) return 0

  const differing: Array<[string, string]> = []
  for (let i = 0; i < aTokens.length; i++) {
    if (aTokens[i] !== bTokens[i]) differing.push([aTokens[i], bTokens[i]])
  }
  if (differing.length !== 1) return 0
  if (!tokensAreVariants(differing[0][0], differing[0][1])) return 0

  const score = 1 - damerauLevenshtein(na, nb) / Math.max(na.length, nb.length)
  return score >= SIMILAR_ROLE_THRESHOLD ? score : 0
}

/** Report-only: (subfunction, role) groups listed more than once, folded for case and whitespace. */
export function findDuplicateRoles(occurrences: RoleOccurrence[]): DuplicateRoleGroup[] {
  const groups = new Map<string, RoleOccurrence[]>()
  for (const occurrence of occurrences) {
    const key = foldRoleKey(occurrence.subfunction, occurrence.role)
    const existing = groups.get(key)
    if (existing) existing.push(occurrence)
    else groups.set(key, [occurrence])
  }

  const result: DuplicateRoleGroup[] = []
  for (const group of groups.values()) {
    if (group.length < 2) continue
    const sheets = new Set(group.map((o) => o.sheet ?? ''))
    result.push({
      subfunction: group[0].subfunction,
      role: group[0].role,
      occurrences: group,
      crossSheet: sheets.size > 1,
    })
  }
  return result
}

/** Recommendation only: distinct role names in one subfunction that may be the same role. */
export function findSimilarRoles(occurrences: RoleOccurrence[]): SimilarRolePair[] {
  const bySubfunction = new Map<string, Map<string, RoleOccurrence>>()
  for (const occurrence of occurrences) {
    const sfKey = foldKey(occurrence.subfunction)
    let roles = bySubfunction.get(sfKey)
    if (!roles) {
      roles = new Map()
      bySubfunction.set(sfKey, roles)
    }
    const roleKey = foldKey(occurrence.role)
    if (!roles.has(roleKey)) roles.set(roleKey, occurrence)
  }

  const pairs: SimilarRolePair[] = []
  for (const roles of bySubfunction.values()) {
    const list = [...roles.values()]
    for (let i = 0; i < list.length; i++) {
      for (let j = i + 1; j < list.length; j++) {
        const score = roleNameSimilarity(list[i].role, list[j].role)
        if (score === 0) continue
        pairs.push({
          subfunction: list[i].subfunction,
          roleA: list[i].role,
          roleB: list[j].role,
          score,
          locationA: { sheet: list[i].sheet, row: list[i].row },
          locationB: { sheet: list[j].sheet, row: list[j].row },
        })
      }
    }
  }
  return pairs
}

/** A vacancy marker such as TBD or N/A is read as a blank cell, so the parser default applies. */
export function normalizeFteCell(raw: string): string {
  const trimmed = raw.trim()
  return VACANCY_MARKERS.has(trimmed.toLowerCase()) ? '' : trimmed
}

/** True when a non-blank FTE cell cannot be read as a non-negative number. */
export function isInvalidFteValue(raw: string): boolean {
  const cell = normalizeFteCell(raw)
  if (!cell) return false
  const parsed = parseFloat(cell)
  return Number.isNaN(parsed) || parsed < 0
}

export function buildInvalidFteNotice(opts: {
  sheet?: string
  row: number
  column: string
  value: string
}): ParseNotice {
  const where = opts.sheet ? `Sheet "${opts.sheet}", row ${opts.row}` : `Row ${opts.row}`
  return {
    code: 'invalid-cell-value',
    sheet: opts.sheet,
    row: opts.row,
    field: 'fte',
    detectedHeader: opts.column,
    message: `${where}, column "${opts.column}": "${opts.value}" is not a valid headcount. Use a number such as 3 or 2.5.`,
  }
}
